import type { DefaultEdgeOptions, Edge, EdgeMarker, EdgeMarkerType } from '../types';

/** Colour of an arrowhead when the marker gives none: the default edge stroke. */
export const DEFAULT_MARKER_COLOR = '#b1b1b7';

/** Arrowhead size in multiples of the edge's stroke width when the marker gives none. */
export const DEFAULT_MARKER_SIZE = 12;

/** A marker with every look filled in, plus the id its SVG `<marker>` is rendered under. */
export type MarkerDefinition = Required<EdgeMarker> & { id: string };

const UNSAFE_ID_CHARACTERS = /[^a-zA-Z0-9_-]/g;

const withDefaults = (marker: EdgeMarker): Required<EdgeMarker> => ({
  type: marker.type,
  color: marker.color ?? DEFAULT_MARKER_COLOR,
  width: marker.width ?? DEFAULT_MARKER_SIZE,
  height: marker.height ?? DEFAULT_MARKER_SIZE,
  strokeWidth: marker.strokeWidth ?? 1,
});

/** Id of the `<marker>` for a marker: equal looks share one id. A string marker is already an id (app-rendered). */
export const getMarkerId = (marker: EdgeMarkerType | undefined): string | null => {
  if (!marker) return null;
  if (typeof marker === 'string') return marker;
  const { type, color, width, height, strokeWidth } = withDefaults(marker);
  return `cm-marker-${type}-${color}-${width}-${height}-${strokeWidth}`.replace(UNSAFE_ID_CHARACTERS, '_');
};

/** The edge as drawn: its own fields over the canvas-wide defaults (v11 `defaultEdgeOptions`). */
export const withDefaultEdgeOptions = (edge: Edge, defaults: DefaultEdgeOptions): Edge => ({ ...defaults, ...edge });

/** Every distinct marker the visible edges use, so each `<marker>` is defined once. */
export const getMarkerDefinitions = (edges: readonly Edge[], defaults: DefaultEdgeOptions): MarkerDefinition[] => {
  const definitions = new Map<string, MarkerDefinition>();
  edges.forEach((edge) => {
    if (edge.hidden) return;
    const { markerStart, markerEnd } = withDefaultEdgeOptions(edge, defaults);
    [markerStart, markerEnd].forEach((marker) => {
      const id = getMarkerId(marker);
      if (id && marker && typeof marker !== 'string' && !definitions.has(id)) definitions.set(id, { ...withDefaults(marker), id });
    });
  });
  return Array.from(definitions.values());
};
